import React, { useCallback, useEffect, useRef, useState } from "react";
import { Loader2, RefreshCw, Search, X } from "lucide-react";

const VIEWS = [
  { id: "live", label: "Live" },
  { id: "history", label: "Fame", ariaLabel: "Hall of fame" },
];

export default function AppLeaderboardChrome({
  embedded = false,
  view,
  onViewChange,
  search,
  onSearchChange,
  loading,
  onRefresh,
  yourRank,
}) {
  const inputRef = useRef(null);
  const searchWrapRef = useRef(null);
  const [searchOpen, setSearchOpen] = useState(false);

  const isLive = view === "live";
  const hasQuery = Boolean(String(search || "").trim());
  const isExpanded = searchOpen || hasQuery;

  const openSearch = useCallback(() => setSearchOpen(true), []);

  const closeSearch = useCallback(() => {
    setSearchOpen(false);
    onSearchChange("");
  }, [onSearchChange]);

  useEffect(() => {
    if (searchOpen && inputRef.current) inputRef.current.focus();
  }, [searchOpen]);

  useEffect(() => {
    if (!isExpanded) return undefined;

    const handlePointerDown = (event) => {
      if (searchWrapRef.current?.contains(event.target)) return;
      if (!hasQuery) setSearchOpen(false);
    };

    const handleKeyDown = (event) => {
      if (event.key !== "Escape") return;
      if (hasQuery) onSearchChange("");
      else setSearchOpen(false);
      inputRef.current?.blur();
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isExpanded, hasQuery, onSearchChange]);

  const handleBlur = () => {
    window.setTimeout(() => {
      if (
        !String(search || "").trim() &&
        !searchWrapRef.current?.contains(document.activeElement)
      ) {
        setSearchOpen(false);
      }
    }, 120);
  };

  const toggleSearch = useCallback(() => {
    if (isExpanded && !hasQuery) {
      setSearchOpen(false);
      return;
    }
    if (isExpanded && hasQuery) {
      closeSearch();
      return;
    }
    openSearch();
  }, [closeSearch, hasQuery, isExpanded, openSearch]);

  return (
    <header
      ref={searchWrapRef}
      className={`alb-chrome alb-chrome-app${embedded ? " alb-chrome-app-embedded" : ""}`}
    >
      <div className="alb-chrome-row">
        <div className="alb-chrome-lead">
          <h1 className="alb-chrome-title">Leaderboard</h1>
          {isLive && yourRank ? (
            <span className="alb-chrome-you tabular-nums">#{yourRank}</span>
          ) : null}
        </div>

        <div className="alb-chrome-tabs" role="tablist" aria-label="Leaderboard views">
          {VIEWS.map(({ id, label, ariaLabel }) => (
            <button
              key={id}
              type="button"
              role="tab"
              aria-label={ariaLabel || label}
              aria-selected={view === id}
              className={`alb-chrome-tab${view === id ? " alb-chrome-tab-active" : ""}`}
              onClick={() => onViewChange(id)}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="alb-chrome-trail">
          {isLive ? (
            <button
              type="button"
              className={`alb-chrome-btn alb-chrome-btn-icon${
                isExpanded ? " alb-chrome-btn-active" : ""
              }`}
              onClick={toggleSearch}
              aria-label={isExpanded ? "Close search" : "Search leaderboard"}
              aria-expanded={isExpanded}
            >
              {isExpanded ? (
                <X size={14} strokeWidth={2.3} aria-hidden />
              ) : (
                <Search size={14} strokeWidth={2.3} aria-hidden />
              )}
            </button>
          ) : null}

          <button
            type="button"
            className="alb-chrome-btn alb-chrome-btn-icon"
            onClick={onRefresh}
            disabled={loading}
            aria-label="Refresh"
            title="Refresh"
          >
            {loading ? (
              <Loader2 size={14} className="animate-spin" aria-hidden />
            ) : (
              <RefreshCw size={14} strokeWidth={2.3} aria-hidden />
            )}
          </button>
        </div>
      </div>

      {isLive && isExpanded ? (
        <div className="alb-chrome-search-panel">
          <Search size={14} aria-hidden className="alb-chrome-search-icon" />
          <input
            ref={inputRef}
            type="search"
            placeholder="Search name or code…"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            onBlur={handleBlur}
            aria-label="Search leaderboard"
          />
          {hasQuery ? (
            <button
              type="button"
              onClick={() => onSearchChange("")}
              aria-label="Clear search"
              className="alb-chrome-search-clear"
            >
              <X size={12} />
            </button>
          ) : null}
        </div>
      ) : null}
    </header>
  );
}
